import { useRef, useContext, useEffect } from 'react';
import { i18n as I18n, withTranslation } from '../utils/i18n';
// components
import Page from '../components/page';
import SearchResults from '../components/searchResults';
import QuickBook from '../components/quickBook';
import Favourites from '../components/favourites';
import FavouriteVenue from '../components/favouriteVenue';
import FavouriteSpace from '../components/favouriteSpace';
import PreviousSearches from '../components/previousSearches';
// context
import { AuthContext } from '../state/auth';
import { FavouritesContext } from '../state/favourites';
import { SearchContext } from '../state/search';

import useStoredBooking from '../hooks/useStoredBooking';

import { handleStickySideBar } from '../utils/stickySidebar';

const RoomsPage = () => {
  const { loggedIn } = useContext(AuthContext);
  const {
    actions: { fetchFavourites, removeFavourite } = {},
    venues: favouriteVenues = [],
    spaces: favouriteSpaces = [],
  } = useContext(FavouritesContext);
  const {
    actions: { applyPreviousSearch },
    previousSearches,
    filters: { date: searchDate },
  } = useContext(SearchContext);

  useEffect(() => {
    if (loggedIn) {
      fetchFavourites('venues');
      fetchFavourites('spaces');
    }
  }, [fetchFavourites, loggedIn]);

  useEffect(() => {
    handleStickySideBar();
  }, []);

  const quickBookRef = useRef({});

  const [
    { spaceIds: storedSpaceIds, venueId: storedVenueId },
    setStoredBooking,
  ] = useStoredBooking();

  const selectSpace = (space, venue) => {
    if (storedSpaceIds.length === 0 || storedVenueId === venue.id) {
      setStoredBooking(storedBooking => ({
        ...storedBooking,
        venueId: venue.id,
        spaceIds: [...storedSpaceIds, space.id],
        date: searchDate || storedBooking.date || new Date(),
      }));
    }

    quickBookRef.current.scrollIntoView({ behaviour: 'smooth' });
    // adding as .scrollIntoView above doesn't scroll enough due to sticky header
    window.scrollBy(0, -120);
  };

  return (
    <Page title="Find a room">
      <main>
        <section>
          <div className="section-content">
            <div className="layout layout--two-col-stacked-left">
              <div className="layout__first">
                <SearchResults
                  selectedVenueId={storedVenueId}
                  selectedSpaceIds={storedSpaceIds}
                  selectSpace={selectSpace}
                />
              </div>
              <div className="layout__last">
                <div className="layout__last--sticky" id="sticky_sidebar">
                  <QuickBook ref={quickBookRef} />

                  {loggedIn && (
                    <Favourites
                      title={I18n.t('favourite.venues.title')}
                      placeholder={I18n.t('favourite.venues.placeholder')}
                      showFavourites={!!favouriteVenues.length}
                    >
                      {favouriteVenues.map(venue => (
                        <FavouriteVenue
                          key={venue.id}
                          venue={venue}
                          onRemove={() => removeFavourite(venue.id, 'venues')}
                        />
                      ))}
                    </Favourites>
                  )}

                  {loggedIn && (
                    <Favourites
                      title={I18n.t('favourite.spaces.title')}
                      placeholder={I18n.t('favourite.spaces.placeholder')}
                      showFavourites={!!favouriteSpaces.length}
                    >
                      {favouriteSpaces.map(space => (
                        <FavouriteSpace
                          key={space.id}
                          space={space}
                          onRemove={() => removeFavourite(space.id, 'spaces')}
                          selectSpace={() => selectSpace(space, space.venue)}
                          navigateToSpace
                        />
                      ))}
                    </Favourites>
                  )}

                  <PreviousSearches
                    previousSearches={previousSearches || []}
                    onClick={applyPreviousSearch}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </Page>
  );
};

RoomsPage.getInitialProps = async () => ({
  namespacesRequired: ['common'],
});

export default withTranslation('common')(RoomsPage);
